/** @jsxImportSource theme-ui */
import React, { useCallback, useState } from 'react';
import { Box, Button, Text } from '@infotrack/zenith-ui';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@/hookform/resolvers/zod';
import { z } from 'zod';
import { usePeriods, type PeriodOption } from '@/features/topfive/hooks/usePeriods';
import {
  formContainerStyles,
  sectionStyles,
  labelStyles,
  dropZoneStyles,
  fileInfoStyles,
  errorTextStyles,
  instructionsBoxStyles,
  instructionsTitleStyles,
  instructionsListStyles,
} from './bonusUploadForm.styles';

// Validation helpers
const validateCsvFile = (file: File): string | null => {
  if (!file.name.endsWith('.csv')) {
    return 'Only CSV files are allowed (.csv extension)';
  }

  const maxSizeBytes = 5 * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return 'File size must not exceed 5MB';
  }

  if (file.size === 0) {
    return 'File cannot be empty';
  }

  return null;
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

// Form validation schema
const BonusUploadFormSchema = z.object({
  file: z.instanceof(File, { message: 'CSV file is required' }),
  periodId: z.string().min(1, { message: 'Please select a period' }),
});

type BonusUploadFormData = z.infer<typeof BonusUploadFormSchema>;

interface BonusUploadFormProps {
  onSubmit: (data: { file: File; startDate: string; endDate: string }) => void;
  isCalculating: boolean;
}

/**
 * CSV file upload form with drag-drop support for bonus calculation.
 * Similar to ImportUploadForm but for bonus calculations.
 */
export const BonusUploadForm: React.FC<BonusUploadFormProps> = ({ onSubmit, isCalculating }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const { data: periods, isLoading: periodsLoading } = usePeriods();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<BonusUploadFormData>({
    resolver: zodResolver(BonusUploadFormSchema),
    defaultValues: {
      periodId: '',
    },
  });

  const periodId = watch('periodId');

  const handleFormSubmit = (data: BonusUploadFormData) => {
    const selectedPeriod = JSON.parse(data.periodId) as PeriodOption;
    onSubmit({
      file: data.file,
      startDate: selectedPeriod.startDate ?? '',
      endDate: selectedPeriod.endDate ?? '',
    });
  };

  const handleFileChange = useCallback(
    (file: File | null) => {
      setFileError(null);

      if (!file) {
        setSelectedFile(null);
        return;
      }

      const error = validateCsvFile(file);
      if (error) {
        setFileError(error);
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setValue('file', file, { shouldValidate: true });
    },
    [setValue]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        handleFileChange(files[0] || null);
      }
    },
    [handleFileChange]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileChange(files[0] || null);
      }
    },
    [handleFileChange]
  );

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Box sx={formContainerStyles}>
        {/* File Upload Zone */}
        <Box sx={sectionStyles}>
          <Box sx={labelStyles}>
            <Text variant="subtitle">CSV File</Text>
          </Box>
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={dropZoneStyles(isDragging, !!fileError)}
            onClick={() => document.getElementById('bonus-file-input')?.click()}
          >
            <input
              id="bonus-file-input"
              type="file"
              accept=".csv"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            {selectedFile ? (
              <Box sx={fileInfoStyles}>
                <Box sx={{ fontWeight: 'bold' }}>
                  <Text variant="medium">{selectedFile.name}</Text>
                </Box>
                <Box sx={{ color: 'neutral/600' }}>
                  <Text variant="caption">{formatFileSize(selectedFile.size)}</Text>
                </Box>
              </Box>
            ) : (
              <Box sx={fileInfoStyles}>
                <Text variant="medium">Drag and drop your CSV file here</Text>
                <Box sx={{ color: 'neutral/600' }}>
                  <Text variant="caption">or click to browse</Text>
                </Box>
              </Box>
            )}
          </Box>
          {fileError && (
            <Box sx={errorTextStyles}>
              <Text variant="caption">{fileError}</Text>
            </Box>
          )}
          {errors.file && (
            <Box sx={errorTextStyles}>
              <Text variant="caption">{errors.file.message as string}</Text>
            </Box>
          )}
        </Box>

        {/* Period Selection */}
        <Box sx={sectionStyles}>
          <Box sx={labelStyles}>
            <Text variant="subtitle">Period</Text>
          </Box>
          <select
            {...register('periodId')}
            disabled={periodsLoading || isCalculating}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: periodsLoading || isCalculating ? '#f3f4f6' : 'white',
              cursor: periodsLoading || isCalculating ? 'not-allowed' : 'pointer',
            }}
          >
            <option value="" disabled hidden>Select Period</option>
            {periods?.map((p) => (
              <option 
                key={`${p.cadenceType}-${p.period}`} 
                value={JSON.stringify(p)}
              >
                {p.displayName} - {p.description}
              </option>
            ))}
          </select>
          {errors.periodId && (
            <Box sx={errorTextStyles}>
              <Text variant="caption">{errors.periodId.message as string}</Text>
            </Box>
          )}
        </Box>

        {/* Submit Button */}
        <Box>
          <Button type="submit" disabled={isCalculating || !selectedFile} fullWidth>
            {isCalculating ? 'Calculating...' : 'Calculate Bonuses'}
          </Button>
        </Box>

        {/* Instructions */}
        <Box sx={instructionsBoxStyles}>
          <Box sx={instructionsTitleStyles}>
            <Text variant="medium">CSV Format Requirements</Text>
          </Box>
          <Box sx={instructionsListStyles}>
            <Text variant="small">• <strong>FirstName:</strong> Staff member first name</Text>
            <Text variant="small">• <strong>Surname:</strong> Staff member surname</Text>
            <Text variant="small">• <strong>EmployeeId:</strong> Staff employee ID</Text>
            <Text variant="small">• <strong>Amount:</strong> Bonus amount to distribute</Text>
          </Box>
        </Box>
      </Box>
    </form>
  );
};
